import { useTranslation } from "@redotech/redo-i18n";
import { RuleTypeDetailsProps } from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { TicketChannelState } from "@redotech/redo-return-ui/flows/rules/ticket-channel";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as conditionsCss from "./conditions.module.css";
import { getRuleChannelLabels } from "./rule-channel-labels";

const channels = [
  ConversationPlatform.EMAIL,
  ConversationPlatform.FACEBOOK,
  ConversationPlatform.INSTAGRAM,
  ConversationPlatform.INSTAGRAM_COMMENTS,
  ConversationPlatform.FACEBOOK_COMMENTS,
  ConversationPlatform.REDO_CHAT,
  ConversationPlatform.ATTENTIVE,
  ConversationPlatform.POSTSCRIPT,
  ConversationPlatform.TIKTOK,
];

export const TicketChannelDetails = memo(function TicketChannelDetails({
  state,
  setState,
  disabled,
}: RuleTypeDetailsProps<TicketChannelState>) {
  const { t } = useTranslation();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const channelItems = useMemo(() => {
    return channels.map((channel) => ({
      id: channel,
      type: "text" as const,
      text: getRuleChannelLabels()[channel],
      value: channel,
    }));
  }, []);

  const selectedItems = useMemo(() => {
    return state.map((channel) => ({
      id: channel,
      type: "text" as const,
      text: getRuleChannelLabels()[channel],
      value: channel,
    }));
  }, [state]);

  const displayText =
    state.length > 0
      ? state.length === 1
        ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          getRuleChannelLabels()[state[0]]
        : t("{{count}} channels selected", { count: state.length })
      : t("Select channels");

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        {t("Channel", { context: "support conversation channel" })}
      </Text>
      <RedoFilterDropdownAnchor
        className={conditionsCss.fullWidthButton}
        color={disabled ? "secondary" : "primary"}
        onClick={() => {
          if (!disabled) {
            setDropdownOpen(!dropdownOpen);
          }
        }}
        ref={setDropdownButtonRef}
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          setState(items.map((item) => item.value));
        }}
        size="xs"
      />
    </Flex>
  );
});
